import { FC } from "react";
import {
  ApplicantStatus,
  ExecutionModes,
  VerificationStatus
} from "shared/types";
import { cx } from "shared/utils";
import {
  AlertCircle,
  CursorClick01,
  Loading02,
  Minus,
  SVGComponentProps
} from "untitledui-js-base";

interface ReportStatusBadgeProps {
  verificationStatus: VerificationStatus;
  applicantStatus?: ApplicantStatus;
  isRequireApproval?: boolean;
  executionMode?: ExecutionModes;
  isActionRequired?: boolean;
}

interface IconProps {
  Icon: FC<SVGComponentProps>;
  className?: string;
}

const Icon = ({ Icon, className }: IconProps) => {
  return <Icon className={className} />;
};

const reportStatusTxt = (status: VerificationStatus) => {
  switch (status) {
    case VerificationStatus.Failed:
      return { title: "Issues with report", icon: AlertCircle };
    case VerificationStatus.FinishedWithWarnings:
      return { title: "Warnings in report", icon: AlertCircle };
    case VerificationStatus.Pending:
      return { title: "Not started", icon: Minus };
    case VerificationStatus.Started:
      return { title: "In progress", icon: Loading02 };
    case VerificationStatus.EmailSent:
      return { title: "Email sent", icon: Loading02 };
  }
};

export const ReportStatusBadge = ({
  verificationStatus,
  applicantStatus,
  isRequireApproval,
  executionMode,
  isActionRequired
}: ReportStatusBadgeProps) => {
  const statusData = reportStatusTxt(verificationStatus);

  if (verificationStatus === VerificationStatus.Skipped)
    return (
      <div className="absolute -top-0 right-0 rounded-bl-lg px-2 py-1.5 border border-t-0 flex items-center bg-background-900 text-content-200">
        <Minus className="w-3.5 h-3.5 mr-2 stroke-content-200" />

        <p className="text-xs font-medium">Wasn&apos;t needed</p>
      </div>
    );

  if (
    executionMode === ExecutionModes.Manual &&
    verificationStatus === VerificationStatus.Pending &&
    applicantStatus === ApplicantStatus.Submitted
  )
    return (
      <div
        className={cx(
          "absolute -top-0 right-0 rounded-bl-lg px-2 py-1.5 border border-t-0 flex gap-2 items-center bg-primary-20 text-primary-500"
        )}
      >
        <CursorClick01 size="20" className="stroke-primary-500" />
        <p className="text-xs font-medium">Awaiting manual run</p>
      </div>
    );

  if (isActionRequired)
    return (
      <div
        className={cx(
          "absolute -top-0 right-0 rounded-bl-lg px-2 py-1.5 border border-t-0 border-alpha-40 flex items-center gap-2 bg-primary-20 text-primary-500"
        )}
      >
        <CursorClick01 className="size-5 stroke-primary-500" />
        <p className="text-xs font-medium">Action required</p>
      </div>
    );

  if (isRequireApproval)
    return (
      <div
        className={cx(
          "absolute -top-0 right-0 rounded-bl-lg px-2 py-1.5 border border-t-0 flex items-center bg-background-900 text-content-200"
        )}
      >
        <p className="text-xs font-medium">Needs conditional offer</p>
      </div>
    );

  if (verificationStatus === VerificationStatus.Finished || !statusData)
    return null;

  const isFailed = verificationStatus === VerificationStatus.Failed;
  const isWarning =
    verificationStatus === VerificationStatus.FinishedWithWarnings;

  return (
    <div
      className={cx(
        "absolute -top-0 right-0 rounded-bl-lg px-2 py-1.5 border border-t-0 flex items-center bg-background-900 text-content-200",
        isFailed && "bg-danger-50 text-danger-500",
        isWarning && "bg-warning-50 text-warning-700"
      )}
    >
      <Icon
        Icon={statusData.icon}
        className={cx(
          "w-3.5 h-3.5 mr-2 stroke-content-200",
          isFailed && "stroke-danger-500",
          isWarning && "stroke-warning-700"
        )}
      />

      <p className="text-xs font-medium">{statusData.title}</p>
    </div>
  );
};
